import SunCalc from "suncalc";
import type { LightSpecification, Map } from "maplibre-gl";
import type { SunUniforms } from "./types";

const RAD_TO_DEG = 180 / Math.PI;
const DEG_TO_RAD = Math.PI / 180;
const EARTH_RADIUS_M = 6_378_137;

function normalizeDegrees(value: number): number {
  return (value % 360 + 360) % 360;
}

function destinationFromBearing(
  latDeg: number,
  lonDeg: number,
  bearingRad: number,
  distanceMeters: number,
): [number, number] {
  const latRad = latDeg * DEG_TO_RAD;
  const dNorth = Math.cos(bearingRad) * distanceMeters;
  const dEast = Math.sin(bearingRad) * distanceMeters;

  const dLat = dNorth / EARTH_RADIUS_M;
  const cosLat = Math.max(0.1, Math.cos(latRad));
  const dLon = dEast / (EARTH_RADIUS_M * cosLat);

  const outLat = latDeg + dLat * RAD_TO_DEG;
  const outLon = lonDeg + dLon * RAD_TO_DEG;
  return [outLon, outLat];
}

export function computeMapLight(date: Date, lat: number, lon: number): LightSpecification {
  const { azimuth, altitude } = SunCalc.getPosition(date, lat, lon);
  const altitudeDeg = altitude * RAD_TO_DEG;
  if (altitudeDeg <= 0) {
    return {
      anchor: "map",
      color: "#dddddd",
      intensity: 0.15,
      position: [100, 180, 90],
    };
  }

  const azimuthDeg = normalizeDegrees(azimuth * RAD_TO_DEG + 180);
  const polarDeg = Math.min(180, Math.max(0, 90 - altitudeDeg));
  const intensity = 0.15;

  return {
    anchor: "map",
    color: "#dddddd",
    intensity,
    position: [100, azimuthDeg, polarDeg],
  };
}

export function computeSunUniforms(date: Date, map: Map): SunUniforms {
  const center = map.getCenter();
  const lat = center.lat;
  const lon = center.lng;
  const { azimuth, altitude } = SunCalc.getPosition(date, lat, lon);
  const azimuthFromNorthRad = azimuth + Math.PI;

  const sourceScreen = map.project([lon, lat]);
  const [targetLon, targetLat] = destinationFromBearing(lat, lon, azimuthFromNorthRad, 75);
  const targetScreen = map.project([targetLon, targetLat]);

  let dx = targetScreen.x - sourceScreen.x;
  let dy = targetScreen.y - sourceScreen.y;
  const magnitude = Math.hypot(dx, dy);

  if (magnitude < 1e-5) {
    dx = Math.sin(azimuthFromNorthRad);
    dy = -Math.cos(azimuthFromNorthRad);
  } else {
    dx /= magnitude;
    dy /= magnitude;
  }

  // Shadow ray-march expects the screen direction from the receiving point toward the sun source.
  // In MapLibre screen space this must be inverted from the projected bearing vector.
  const directionScreen: [number, number] = [dx, -dy];
  const tanAltitude = altitude > 0 ? Math.tan(altitude) : -1;

  return {
    azimuthRad: azimuth,
    altitudeRad: altitude,
    tanAltitude,
    directionScreen,
  };
}
